import { AxiosError, isAxiosError, AxiosResponse } from 'axios';
interface ErrorMessageBody {
  message?: string;
}

export const handleApiError = (error: unknown, defaultMessage: string): Error => {
  let errorMessage = defaultMessage;

  if (isAxiosError(error) && error.response?.data?.message) {
    errorMessage = error.response.data.message;
  } else if (error instanceof Error && !isAxiosError(error)) {
    errorMessage = error.message;
  }

  return new Error(errorMessage);
};

export const isNotFoundError = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 404;

export const extractApiErrorMessage = (error: unknown): string | undefined => {
  if (!isAxiosError(error)) {
    return undefined;
  }

  const message = (error.response?.data as ErrorMessageBody | undefined)?.message;
  return typeof message === 'string' && message.trim() ? message : undefined;
};

export const extractApiErrorCode = (error: unknown): string | undefined => {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }

  const data = (error as { response?: { data?: unknown } }).response?.data;

  if (typeof data !== 'object' || data === null) {
    return undefined;
  }

  const { errorCode } = data as { errorCode?: unknown };

  return typeof errorCode === 'string' ? errorCode : undefined;
};

/**
 * Reads the positions an API reports for a name conflict. They index the array as sent, so a
 * malformed entry must not shift the ones that follow: anything that is not a non-negative integer
 * is dropped instead of being coerced.
 */
export const extractApiConflictingIndexes = (error: unknown): number[] => {
  if (typeof error !== 'object' || error === null) {
    return [];
  }

  const data = (error as { response?: { data?: unknown } }).response?.data;

  if (typeof data !== 'object' || data === null) {
    return [];
  }

  const { conflictingIndexes } = data as { conflictingIndexes?: unknown };

  if (!Array.isArray(conflictingIndexes)) {
    return [];
  }

  return conflictingIndexes.filter(
    (index): index is number => typeof index === 'number' && Number.isInteger(index) && index >= 0
  );
};

export const extractApiFieldErrors = (error: unknown): Record<string, string[]> => {
  if (!isAxiosError(error)) {
    return {};
  }

  const fieldErrors = error.response?.data?.errors;

  if (typeof fieldErrors !== 'object' || fieldErrors === null || Array.isArray(fieldErrors)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(fieldErrors as Record<string, unknown>).filter(
      (entry): entry is [string, string[]] =>
        Array.isArray(entry[1]) && entry[1].every((message) => typeof message === 'string')
    )
  );
};

export const extractAxiosBlobErrorMessage = async (
  error: unknown,
  fallbackMessage: string
): Promise<string | null> => {
  if (!(isAxiosError(error) && error.response?.data instanceof Blob)) {
    return null;
  }

  const text = await error.response.data.text();

  try {
    const body = JSON.parse(text) as ErrorMessageBody;
    return body.message ?? fallbackMessage;
  } catch {
    return fallbackMessage;
  }
};

export type ExcelBlobExportResult = {
  blob: Blob;
  exportedCount: number;
  filename: string;
};

export function excelBlobExportResultFromAxiosResponse(
  response: AxiosResponse<Blob>,
  defaultFilename: string
): ExcelBlobExportResult {
  const exportedCount = Number.parseInt(String(response.headers['x-exported-count'] ?? '0'), 10);
  const disposition = String(response.headers['content-disposition'] ?? '');
  const match = /filename="([^"]+)"/.exec(disposition);
  const filename = match?.[1] ?? defaultFilename;
  return {
    blob: response.data,
    exportedCount,
    filename,
  };
}

export async function requestExcelBlobExport(
  request: () => Promise<AxiosResponse<Blob>>,
  defaultFilename: string,
  genericErrorMessage: string
): Promise<ExcelBlobExportResult> {
  try {
    const response = await request();
    return excelBlobExportResultFromAxiosResponse(response, defaultFilename);
  } catch (error) {
    const blobMessage = await extractAxiosBlobErrorMessage(error, genericErrorMessage);
    if (blobMessage) {
      throw new Error(blobMessage);
    }
    throw error;
  }
}

export async function requestPdfBlobExport(
  request: () => Promise<AxiosResponse<Blob>>,
  genericErrorMessage: string
): Promise<Blob> {
  try {
    const response = await request();
    return response.data;
  } catch (error) {
    const blobMessage = await extractAxiosBlobErrorMessage(error, genericErrorMessage);
    if (blobMessage) {
      throw new Error(blobMessage);
    }
    throw error;
  }
}

export const isSearchRelatedError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;

  const axiosError = error as AxiosError;

  if (!axiosError.response) {
    return true;
  }

  const status = axiosError.response.status;
  const responseData = axiosError.response.data as any;

  if (status === 400) {
    const message = responseData?.message ?? '';
    return !(
      message.includes('page') ||
      message.includes('página') ||
      message.includes('não encontrado') ||
      message.includes('not found')
    );
  }

  if (status === 404) {
    return false;
  }

  if (status >= 500) {
    return true;
  }

  if (status === 401 || status === 403) {
    return true;
  }

  return true;
};

export const isNetworkError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;

  const axiosError = error as AxiosError;

  return (
    !axiosError.response ||
    axiosError.code === 'NETWORK_ERROR' ||
    axiosError.code === 'ECONNABORTED' ||
    axiosError.message.includes('Network Error')
  );
};

export const shouldShowErrorToast = (error: unknown): boolean => {
  if (!isSearchRelatedError(error)) {
    return false;
  }

  const axiosError = error as AxiosError;

  if (isNetworkError(error)) {
    return true;
  }

  const status = axiosError?.response?.status;

  if (status === 500 || status === 401 || status === 403) {
    return true;
  }

  if (status === 400) {
    return true;
  }

  return false;
};
